import { useDebounceFn } from "@vueuse/core";
import { useGlobalStore } from "@/stores/globalStore";
import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import useApiHelpers from "@/composables/api/helpers";
import usePaymentFilters from "@/composables/filters/usePaymentFilters";

const { postRequest } = useApiHelpers();
const { filterPaymentDataByPaymentName } = usePaymentFilters();

const fieldsFromCalcApiRelatedToPayload = [
  "coinAmount",
  "coinType",
  "fiatAmount",
  "fiatType",
  "lockFiat",
];
const fieldsFromCalcApiRelatedToFeeData = [
  "networkFee",
  "gatewayFee",
  "receivedRate",
  "quoteValue",
];
let skipStoreUpdateByLockFiat;

export default function useDynamicPricingCalculator() {
  // get payment data related values
  const getPaymentData = async (
    globalStore,
    orderStore,
    paymentName,
    fiatType
  ) => {
    const emptyPaymentData = {
      payment: null,
      paymentId: null,
    };
    const paymentData = orderStore.getActiveStorePaymentData;

    if (!paymentName) {
      return emptyPaymentData;
    }

    // race condition fix to prevent incorrect fiatTYpe + payment watch from triggering between
    // buy/sell mode toggles and throwing a 419 for the payment + fiatType combo
    // not allowed in the API
    const isFiatAllowedByPaymentData = (payment) => {
      if (
        payment.length <= 0 ||
        !payment[0].supported_fiat.includes(fiatType)
      ) {
        return "";
      }

      return payment;
    };

    const data = isFiatAllowedByPaymentData(
      filterPaymentDataByPaymentName(paymentData, paymentName)
    );

    if (!data || data.length <= 0) {
      return emptyPaymentData;
    }

    const { code, id } = data[0];
    return {
      payment: code,
      paymentId: id,
    };
  };

  /**
   * Due to the calculator requiring both the coin and fiatAmount to be set,
   * we change null/undefined values to zero
   * @param {Object} coinAmount
   * @param {Object} fiatAmount
   * @returns {Object}
   * */
  const changeNullValuesToZero = (coinAmount, fiatAmount) => {
    const toChange = { coinAmount, fiatAmount };

    Object.keys(toChange).forEach((field) => {
      if (
        toChange[field] === "" ||
        toChange[field] === null ||
        typeof toChange[field] === "undefined"
      ) {
        toChange[field] = 0;
      }
    });

    return toChange;
  };

  const callPricingApi = useDebounceFn(async (payload, form, store, globalStore) => {
    await postRequest(
      "/pricing/calculate",
      payload,
      (response) => {
        const { data } = response;
        Object.entries(data).forEach(([key, value]) => {
          if (
            fieldsFromCalcApiRelatedToPayload.includes(key) &&
            key !== skipStoreUpdateByLockFiat
          ) {
            form.value[key] = value;
            store.updateValue(key, value);
          } else if (fieldsFromCalcApiRelatedToFeeData.includes(key)) {
            store.updateFeeData(key, value);
          }
        });
      },
      null,
      [],
      false
    );
    store.updateFeeData("isLoading", false);
    globalStore.setShowSplashScreen(false);
  }, 800);

  /**
   * Watches a value and updates a chosen Pinia store with the values
   * @param {object} form The form that's values are being watched.
   * @param {object} store The (buy or sell) form  store that the state is to be bound to
   * @param {function} callback OPTIONAL. Callback to run when complete.
   */
  const dynamicPricingCalculator = async (key, val, form, store) => {
    const globalStore = useGlobalStore();
    const orderStore = useOrderFormStore();
    const { walletAddress, walletAddressTag, ...rest } = form.value;

    if (key === "fiatAmount") {
      store.updateValue("lockFiat", true);
    } else if (key === "coinAmount") {
      store.updateValue("lockFiat", false);
    }

    const paymentData = await getPaymentData(
      globalStore,
      orderStore,
      form.value.payment,
      form.value.fiatType
    );

    const defaults = {
      orderType: orderStore.getActiveMode,
      lockFiat: store.form.lockFiat.value,
      ...paymentData,
    };

    const calculatorFields = {
      ...rest,
      ...defaults,
    };

    const triggerCalculatorOnFields =
      Object.keys(calculatorFields).includes(key);

    if (!val || !triggerCalculatorOnFields || !globalStore.$state.token) {
      return;
    }

    const { fiatAmount, coinAmount, ...restFields } = calculatorFields;
    const trimmedValues = changeNullValuesToZero(coinAmount, fiatAmount);
    const newPayload = { ...trimmedValues, ...restFields };

    skipStoreUpdateByLockFiat = key;

     // Set blockchain value from its object
     if (newPayload.blockchain?.value) {
      newPayload.blockchain = newPayload.blockchain.value;
    }

    if (
      (fiatAmount && !Number.isNaN(fiatAmount) && parseFloat(fiatAmount) > 0) ||
      (coinAmount && !Number.isNaN(coinAmount) && parseFloat(coinAmount) > 0)
    ) {
      store.updateFeeData("isLoading", true);
      callPricingApi(newPayload, form, store, globalStore);
    }
  };

  return {
    dynamicPricingCalculator,
  };
}
